import React from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useConversion } from '../context/ConversionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiImage, FiZap, FiShield, FiCloud, FiFile, FiDownload, FiUpload, FiRefreshCw, FiMusic, FiVideo, FiArchive, FiBook, FiGrid, FiDatabase, FiCode, FiLayout, FiPenTool, FiArrowRight, FiLock, FiCheck, FiAlertCircle, FiGithub } from 'react-icons/fi';
import FileUpload from './FileUpload';
import ImagesToPdf from './ImagesToPdf';
import PdfToImages from './PdfToImages';
import HowItWorks from './HowItWorks';
import UnitConversionCalculator from './UnitConversion/UnitConversionCalculator';
import { useState, useEffect, useMemo, useRef } from 'react';
import { DE } from 'country-flag-icons/react/3x2';
import { 
  AiOutlineFilePdf, 
  AiOutlineFileWord, 
  AiOutlineFileExcel, 
  AiOutlineFilePpt,
  AiOutlineFileImage,
  AiOutlineFileMarkdown,
  AiOutlineFileZip,
  AiOutlineFileText,
  AiOutlineFileJpg,
  AiOutlineFileGif,
  AiFillHtml5,
  AiOutlineFileUnknown,
  AiOutlineFile,  // Generic file icon
  AiOutlineAudio,  // For audio files
  AiOutlineVideoCamera,  // For video files
  AiOutlineFileSync,  // For conversion icons
  AiOutlineDatabase,  // For data files
  AiOutlineHtml5,
  AiOutlineCopy,
  AiOutlineBook  // For CAD files
} from 'react-icons/ai';
import { 
  SiMicrosoft,
  SiMicrosoftword,
  SiMicrosoftexcel,
  SiMicrosoftpowerpoint,
  SiAdobeacrobatreader,
  SiAdobecreativecloud
} from 'react-icons/si';
import { conversionColorMapping } from '../constants/colorMapping';
import { Helmet } from 'react-helmet-async';
import { getConversionMeta, getConversionSlug } from '../utils/seoMetadata';
import { trackConversion } from '../utils/analytics';
import { useDarkMode } from '../context/DarkModeContext';
import { log } from '../utils/logger';

// First, import the images directly
import decimalBase64Screenshot from '../assests/screenshots/UnitConversion/decimal-base64.png';
import binaryOpsScreenshot from '../assests/screenshots/UnitConversion/binary-ops.png';
import scientificScreenshot from '../assests/screenshots/UnitConversion/scientific.png';
import encodingTablesScreenshot from '../assests/screenshots/UnitConversion/encoding-tables.png';
import bitGroupingScreenshot from '../assests/screenshots/UnitConversion/bit-grouping.png';
import interactiveModeScreenshot from '../assests/screenshots/UnitConversion/interactive-mode.png';

const FeatureCard = ({ icon, title, description }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="bg-white p-6 rounded-lg shadow-lg"
  >
    <div className="text-4xl mb-4 text-blue-500">{icon}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

const ConversionCategory = ({ title, items }) => {
  const { darkMode } = useDarkMode();
  // Only group PDFs in the DOCUMENT category
  const shouldGroupPDF = title === "DOCUMENT";
  
  const groupedItems = shouldGroupPDF ? items.reduce((acc, item) => {
    if (item.name.toLowerCase().includes('pdf')) {
      if (!acc.pdfRelated) acc.pdfRelated = [];
      acc.pdfRelated.push(item);
    } else {
      if (!acc.others) acc.others = [];
      acc.others.push(item);
    }
    return acc;
  }, {}) : { others: items };

  return (
    <div className="min-w-[160px] flex-1">
      <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4
        ${darkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>{title}</h3>
      
      {/* PDF Group only in DOCUMENT category */}
      {shouldGroupPDF && groupedItems.pdfRelated && groupedItems.pdfRelated.length > 0 && (
        <div className="mb-6">
          <div className={`text-xs font-medium mb-2
            ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>PDF Conversions</div>
          <div className="space-y-2.5">
            {groupedItems.pdfRelated.map((item, index) => (
              <ConversionItem 
                key={index} 
                item={{
                  ...item,
                  popular: ['PDF to Word', 'PDF to Excel', 'Word to PDF'].includes(item.name)
                }} 
                isPDF={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other items */}
      {groupedItems.others && (
        <div className="space-y-2.5">
          {groupedItems.others.map((item, index) => (
            <ConversionItem 
              key={index} 
              item={{
                ...item,
                popular: ['JPEG to PNG', 'MP4 to MP3'].includes(item.name)
              }} 
              isPDF={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ConversionItem = ({ item, isPDF }) => {
  const { setSelectedConversion, currentTheme, isLoading, setIsLoading } = useConversion();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();

  const handleConversionClick = async () => {
    if (conversionColorMapping[item.value]) {
      setIsLoading(true);
      trackConversion(item.value, 'menu_click');
      
      setSelectedConversion(item.value);
      
      const conversionSlug = item.slug || getConversionSlug(item.value);
      await navigate(`/convert/${conversionSlug}`, { replace: true });

      const uploadArea = document.querySelector('#file-upload-area');
      if (uploadArea) {
        const navbarHeight = 64;
        const additionalOffset = 80; // Match the offset we used for reload
        const elementPosition = uploadArea.getBoundingClientRect().top;
        const offsetPosition = elementPosition - navbarHeight - additionalOffset;

        window.scrollTo({
          top: window.scrollY + offsetPosition,
          behavior: 'smooth'
        });
      }
      
      setTimeout(() => setIsLoading(false), 300);
    } else {
      console.warn(`No theme found for conversion: ${item.value}`);
      setSelectedConversion('default');
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02, x: 2 }}
      whileTap={{ scale: 0.98 }}
      className={`group relative transition-colors duration-300
        ${darkMode ? 'hover:bg-gray-800/80' : 'hover:bg-white/80'}
      `}
      onClick={handleConversionClick}
    >
      {/* Loading overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-white/80 rounded-lg z-10
                     flex items-center justify-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`flex items-center gap-3 p-2 rounded-lg border border-transparent cursor-pointer
          transition-all duration-200
          ${isPDF ? (darkMode ? 'bg-primary-900/10' : 'bg-primary-50/10') : (darkMode ? 'bg-gray-900/80' : 'bg-neutral-50')}
          ${darkMode ? 'border-gray-800' : 'hover:border-neutral-100'}
        `}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-200
          ${isPDF ? (darkMode ? 'bg-primary-900/20' : 'from-primary-50 to-primary-100/50') : (darkMode ? 'bg-gray-800/60' : 'from-neutral-50 to-neutral-100')}
        `}>
          {item.icon}
        </div>
        <span className={`text-sm font-medium truncate transition-colors duration-300
          ${darkMode ? 'text-neutral-100 group-hover:text-neutral-50' : 'text-neutral-700 group-hover:text-neutral-900'}`}>{item.name}</span>
        {item.popular && (
          <span className={`ml-auto shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full
            ${darkMode ? 'text-emerald-300 bg-emerald-900/30' : 'text-accent-700 bg-accent-50'}`}>POPULAR</span>
        )}
        {item.new && (
          <span className={`ml-auto shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full
            ${darkMode ? 'text-primary-300 bg-primary-900/30' : 'text-primary-700 bg-primary-50'}`}>NEW</span>
        )}
      </div>
    </motion.div>
  );
};

// Update your existing conversionOptions object
const conversionOptions = {
  document: [
    {
      name: "PDF to Word",
      value: "pdfToWord",
      slug: "pdf-to-word",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFilePdf className="w-5 h-5 text-red-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to Word | Free Online PDF to DOCX Converter - iconvertify",
        description: "Convert PDF to Word documents online. Free, secure PDF to DOCX conversion with high accuracy and quality preservation.",
        keywords: "pdf to word, convert pdf to docx, pdf converter, document conversion",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to Word Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "Word to PDF", 
      value: "wordToPdf",
      slug: "word-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileWord className="w-5 h-5 text-blue-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert Word to PDF | Free DOCX to PDF Converter - iconvertify",
        description: "Convert Word documents to PDF online. Free, secure DOCX to PDF conversion that maintains formatting and layout.",
        keywords: "word to pdf, convert docx to pdf, document converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "Word to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "Excel to PDF",
      value: "excelToPdf",
      slug: "excel-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileExcel className="w-5 h-5 text-green-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert Excel to PDF | Free Online XLSX to PDF Converter - iconvertify",
        description: "Convert Excel spreadsheets to PDF online. Free, secure XLSX to PDF conversion that preserves formatting and layout.",
        keywords: "excel to pdf, convert xlsx to pdf, spreadsheet converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "Excel to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PDF to Excel",
      value: "pdfToExcel", 
      slug: "pdf-to-excel",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileExcel className="w-5 h-5 text-green-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to Excel | Free Online PDF to XLSX Converter - iconvertify",
        description: "Convert PDF documents to Excel spreadsheets online. Free, secure PDF to XLSX conversion that preserves data integrity.",
        keywords: "pdf to excel, convert pdf to xlsx, pdf converter, spreadsheet converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to Excel Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PowerPoint to PDF",
      value: "pptToPdf",
      slug: "ppt-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFilePpt className="w-5 h-5 text-orange-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PowerPoint to PDF | Free Online PPTX to PDF Converter - iconvertify",
        description: "Convert PowerPoint presentations to PDF online. Free, secure PPTX to PDF conversion that preserves formatting and layout.",
        keywords: "powerpoint to pdf, convert pptx to pdf, presentation converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PowerPoint to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PDF to PowerPoint",
      value: "pdfToPpt",
      slug: "pdf-to-ppt",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFilePpt className="w-5 h-5 text-orange-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to PowerPoint | Free Online PDF to PPTX Converter - iconvertify",
        description: "Convert PDF documents to PowerPoint presentations online. Free, secure PDF to PPTX conversion that preserves data integrity.",
        keywords: "pdf to powerpoint, convert pdf to pptx, pdf converter, presentation converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to PowerPoint Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "Text to PDF",
      value: "textToPdf",
      slug: "text-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileText className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert Text to PDF | Free Online Text to PDF Converter - iconvertify",
        description: "Convert plain text documents to PDF online. Free, secure text to PDF conversion that preserves formatting and layout.",
        keywords: "text to pdf, convert text to pdf, document converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "Text to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PDF to Text",
      value: "pdfToText",
      slug: "pdf-to-text",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileText className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to Text | Free Online PDF to Text Converter - iconvertify",
        description: "Convert PDF documents to plain text online. Free, secure PDF to text conversion that preserves data integrity.",
        keywords: "pdf to text, convert pdf to text, pdf converter, document converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to Text Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "Markdown to PDF",
      value: "mdToPdf",
      slug: "md-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileMarkdown className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert Markdown to PDF | Free Online MD to PDF Converter - iconvertify",
        description: "Convert Markdown documents to PDF online. Free, secure MD to PDF conversion that preserves formatting and layout.",
        keywords: "markdown to pdf, convert md to pdf, document converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "Markdown to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PDF to Markdown",
      value: "pdfToMd",
      slug: "pdf-to-md",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileMarkdown className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to Markdown | Free Online PDF to MD Converter - iconvertify",
        description: "Convert PDF documents to Markdown online. Free, secure PDF to MD conversion that preserves data integrity.",
        keywords: "pdf to markdown, convert pdf to md, pdf converter, document converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to Markdown Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  image: [
    {
      name: "JPEG to PNG",
      value: "jpegToPng",
      slug: "jpeg-to-png",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileJpg className="w-5 h-5 text-fuchsia-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert JPEG to PNG | Free Online Image Converter - iconvertify",
        description: "Convert JPEG images to PNG format online. Free, high-quality image conversion with transparency support.",
        keywords: "jpeg to png, convert jpeg to png, image converter, png converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "JPEG to PNG Converter",
          "applicationCategory": "Image Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PNG to JPEG",
      value: "pngToJpeg",
      slug: "png-to-jpeg",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileImage className="w-5 h-5 text-indigo-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PNG to JPEG | Free Online Image Converter - iconvertify",
        description: "Convert PNG images to JPEG format online. Free, high-quality image conversion with quality control.",
        keywords: "png to jpeg, convert png to jpeg, image converter, jpeg converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PNG to JPEG Converter",
          "applicationCategory": "Image Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "GIF to PNG",
      value: "gifToPng",
      slug: "gif-to-png",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileGif className="w-5 h-5 text-pink-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert GIF to PNG | Free Online Image Converter - iconvertify",
        description: "Convert GIF animations to PNG format online. Free, high-quality image conversion with frame extraction.",
        keywords: "gif to png, convert gif to png, image converter, png converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "GIF to PNG Converter",
          "applicationCategory": "Image Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "SVG to PNG",
      value: "svgToPng",
      slug: "svg-to-png",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileImage className="w-5 h-5 text-cyan-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert SVG to PNG | Free Online Image Converter - iconvertify",
        description: "Convert SVG vector graphics to PNG format online. Free, high-quality image conversion with rasterization.",
        keywords: "svg to png, convert svg to png, image converter, png converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "SVG to PNG Converter",
          "applicationCategory": "Image Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "WEBP to PNG",
      value: "webpToPng",
      slug: "webp-to-png",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileImage className="w-5 h-5 text-teal-500" />
          </div>
        </div>
      ),
      new: true,
      seo: {
        title: "Convert WEBP to PNG | Free Online Image Converter - iconvertify",
        description: "Convert WEBP images to PNG format online. Free, high-quality image conversion with lossless quality preservation.",
        keywords: "webp to png, convert webp to png, image converter, png converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "WEBP to PNG Converter",
          "applicationCategory": "Image Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  audioVideo: [
    {
      name: "MP4 to MP3",
      value: "mp4ToMp3",
      slug: "mp4-to-mp3",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineVideoCamera className="w-5 h-5 text-red-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert MP4 to MP3 | Free Online Audio Extractor - iconvertify",
        description: "Extract audio from MP4 videos online. Free, high-quality audio extraction with customizable settings.",
        keywords: "mp4 to mp3, convert mp4 to mp3, audio extractor, mp3 converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "MP4 to MP3 Converter",
          "applicationCategory": "Audio Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "MP3 to MP4",
      value: "mp3ToMp4",
      slug: "mp3-to-mp4",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineAudio className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert MP3 to MP4 | Free Online Audio to Video Converter - iconvertify",
        description: "Convert MP3 audio files to MP4 videos online. Free, high-quality audio to video conversion with customizable settings.",
        keywords: "mp3 to mp4, convert mp3 to mp4, audio converter, video converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "MP3 to MP4 Converter",
          "applicationCategory": "Video Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "AVI to MP4",
      value: "aviToMp4",
      slug: "avi-to-mp4",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineVideoCamera className="w-5 h-5 text-red-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert AVI to MP4 | Free Online Video Converter - iconvertify",
        description: "Convert AVI videos to MP4 format online. Free, high-quality video conversion with customizable settings.",
        keywords: "avi to mp4, convert avi to mp4, video converter, mp4 converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "AVI to MP4 Converter",
          "applicationCategory": "Video Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "MP4 to GIF",
      value: "mp4ToGif",
      slug: "mp4-to-gif",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileGif className="w-5 h-5 text-pink-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert MP4 to GIF | Free Online Video to Animated GIF Converter - iconvertify",
        description: "Convert MP4 videos to animated GIFs online. Free, high-quality video to GIF conversion with customizable settings.",
        keywords: "mp4 to gif, convert mp4 to gif, video converter, gif converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "MP4 to GIF Converter",
          "applicationCategory": "Video Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  archive: [
    {
      name: "ZIP to RAR",
      value: "zipToRar",
      slug: "zip-to-rar",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileZip className="w-5 h-5 text-amber-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert ZIP to RAR | Free Online Archive Converter - iconvertify",
        description: "Convert ZIP archives to RAR format online. Free, high-quality archive conversion with customizable settings.",
        keywords: "zip to rar, convert zip to rar, archive converter, rar converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "ZIP to RAR Converter",
          "applicationCategory": "Archive Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "RAR to ZIP",
      value: "rarToZip",
      slug: "rar-to-zip",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileZip className="w-5 h-5 text-orange-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert RAR to ZIP | Free Online Archive Converter - iconvertify",
        description: "Convert RAR archives to ZIP format online. Free, high-quality archive conversion with customizable settings.",
        keywords: "rar to zip, convert rar to zip, archive converter, zip converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "RAR to ZIP Converter",
          "applicationCategory": "Archive Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "7Z to ZIP",
      value: "sevenZipToZip",
      slug: "7z-to-zip",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileZip className="w-5 h-5 text-yellow-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert 7Z to ZIP | Free Online Archive Converter - iconvertify",
        description: "Convert 7Z archives to ZIP format online. Free, high-quality archive conversion with customizable settings.",
        keywords: "7z to zip, convert 7z to zip, archive converter, zip converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "7Z to ZIP Converter",
          "applicationCategory": "Archive Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "ZIP to 7Z",
      value: "zipToSevenZip",
      slug: "zip-to-7z",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileZip className="w-5 h-5 text-amber-600" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert ZIP to 7Z | Free Online Archive Converter - iconvertify",
        description: "Convert ZIP archives to 7Z format online. Free, high-quality archive conversion with customizable settings.",
        keywords: "zip to 7z, convert zip to 7z, archive converter, 7z converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "ZIP to 7Z Converter",
          "applicationCategory": "Archive Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  ebook: [
    {
      name: "EPUB to PDF",
      value: "epubToPdf",
      slug: "epub-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineBook className="w-5 h-5 text-purple-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert EPUB to PDF | Free Online Ebook Converter - iconvertify",
        description: "Convert EPUB ebooks to PDF format online. Free, high-quality ebook conversion with customizable settings.",
        keywords: "epub to pdf, convert epub to pdf, ebook converter, pdf converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "EPUB to PDF Converter",
          "applicationCategory": "Ebook Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "PDF to EPUB",
      value: "pdfToEpub",
      slug: "pdf-to-epub",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineBook className="w-5 h-5 text-green-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert PDF to EPUB | Free Online Ebook Converter - iconvertify",
        description: "Convert PDF documents to EPUB ebooks online. Free, high-quality ebook conversion with customizable settings.",
        keywords: "pdf to epub, convert pdf to epub, ebook converter, epub converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "PDF to EPUB Converter",
          "applicationCategory": "Ebook Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  cadDesign: [
    {
      name: "DWG to PDF",
      value: "dwgToPdf",
      slug: "dwg-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineCopy className="w-5 h-5 text-cyan-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert DWG to PDF | Free Online CAD Converter - iconvertify",
        description: "Convert DWG CAD files to PDF format online. Free, high-quality CAD conversion with customizable settings.",
        keywords: "dwg to pdf, convert dwg to pdf, cad converter, pdf converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "DWG to PDF Converter",
          "applicationCategory": "CAD Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "DXF to PDF",
      value: "dxfToPdf",
      slug: "dxf-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineCopy className="w-5 h-5 text-teal-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert DXF to PDF | Free Online CAD Converter - iconvertify",
        description: "Convert DXF CAD files to PDF format online. Free, high-quality CAD conversion with customizable settings.",
        keywords: "dxf to pdf, convert dxf to pdf, cad converter, pdf converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "DXF to PDF Converter",
          "applicationCategory": "CAD Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  data: [
    {
      name: "CSV to JSON",
      value: "csvToJson",
      slug: "csv-to-json",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineDatabase className="w-5 h-5 text-emerald-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert CSV to JSON | Free Online Data Converter - iconvertify",
        description: "Convert CSV data to JSON format online. Free, high-quality data conversion with customizable settings.",
        keywords: "csv to json, convert csv to json, data converter, json converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "CSV to JSON Converter",
          "applicationCategory": "Data Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "JSON to CSV",
      value: "jsonToCsv",
      slug: "json-to-csv",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineDatabase className="w-5 h-5 text-violet-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert JSON to CSV | Free Online Data Converter - iconvertify",
        description: "Convert JSON data to CSV format online. Free, high-quality data conversion with customizable settings.",
        keywords: "json to csv, convert json to csv, data converter, csv converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "JSON to CSV Converter",
          "applicationCategory": "Data Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
  other: [
    {
      name: "HTML to PDF",
      value: "htmlToPdf",
      slug: "html-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineHtml5 className="w-5 h-5 text-orange-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert HTML to PDF | Free Online Document Converter - iconvertify",
        description: "Convert HTML documents to PDF format online. Free, high-quality document conversion with customizable settings.",
        keywords: "html to pdf, convert html to pdf, document converter, pdf converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "HTML to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
    {
      name: "Markdown to PDF",
      value: "mdToPdf",
      slug: "md-to-pdf",
      icon: (
        <div className="w-8 h-8 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm flex items-center justify-center">
            <AiOutlineFileMarkdown className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      ),
      seo: {
        title: "Convert Markdown to PDF | Free Online Document Converter - iconvertify",
        description: "Convert Markdown documents to PDF format online. Free, high-quality document conversion with customizable settings.",
        keywords: "markdown to pdf, convert markdown to pdf, document converter, pdf converter",
        schema: {
          "@type": "SoftwareApplication",
          "name": "Markdown to PDF Converter",
          "applicationCategory": "Document Converter",
          "operatingSystem": "Web Browser"
        }
      }
    },
  ],
};

// Update the screenshots object
const screenshots = {
  decimalToBase64: decimalBase64Screenshot,
  binaryOperations: binaryOpsScreenshot,
  scientificNotation: scientificScreenshot,
  encodingTables: encodingTablesScreenshot,
  bitGrouping: bitGroupingScreenshot,
  interactiveMode: interactiveModeScreenshot
};

const ExplanationShowcase = () => {
  const [activeDemo, setActiveDemo] = useState(0);
  
  const demos = [
    {
      type: 'number',
      from: 'Decimal',
      to: 'Base-64',
      value: '255',
      title: 'Base64 Encoding'
    },
    {
      type: 'number',
      from: 'Binary',
      to: 'Hexadecimal',
      value: '10101010',
      title: 'Binary → Hex'
    },
    {
      type: 'number',
      from: 'Decimal',
      to: 'Binary',
      value: '42',  // A good example number that shows binary conversion clearly
      title: 'Decimal → Binary'
    },
    {
      type: 'number',
      from: 'Decimal',
      to: 'Scientific Notation',
      value: '299792458',
      title: 'Scientific'
    }
  ];

  useEffect(() => {
    // Reset the calculator when switching demos
    if (activeDemo >= 0 && activeDemo < demos.length) {
      // The calculator will handle the initial state
    }
  }, [activeDemo]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-6">
        {demos.map((demo, index) => (
          <button
            key={index}
            onClick={() => setActiveDemo(index)}
            className={`px-4 py-1.5 text-sm rounded-lg transition-all duration-200
                      ${activeDemo === index 
                        ? 'bg-emerald-100 text-emerald-600 font-medium' 
                        : 'text-gray-500 hover:bg-gray-100'}`}
          >
            {demo.title}
          </button>
        ))}
      </div>

      <motion.div
        key={activeDemo}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <UnitConversionCalculator
          initialSelectedConversion={demos[activeDemo].type}
          initialFromUnit={demos[activeDemo].from}
          initialToUnit={demos[activeDemo].to}
          placeholder={demos[activeDemo].value}
          previewMode={true}
          showExplanation={true}
          darkMode={false}
        />
      </motion.div>
    </div>
  );
};

const Home = () => {
  const { conversionType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedConversion, conversionTitle } = useConversion();
  const { darkMode } = useDarkMode();

  // Check if we should render special components
  const isImagesToPdf = location.pathname === '/images-to-pdf';
  const isPdfToImages = location.pathname === '/pdf-to-images';

  useEffect(() => {
    // REMOVE or comment out the personal log for Margot
    // log(
    //   heart,
    //   'color: #8B0000; font-size: 14px; font-style: italic;',
    //   'color: #8B0000; font-size: 14px; font-style: italic;',
    //   'color: #8B0000; font-size: 14px; font-style: italic;'
    // );
  }, []);

  useEffect(() => {
    if (conversionType) {
      // Convert from kebab-case back to camelCase for the theme
      const camelCaseType = conversionType
        .split('-')
        .map((word, index) => 
          index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join('');
      
      setSelectedConversion(camelCaseType);

      // This handles the reload behavior correctly
      const uploadArea = document.querySelector('#file-upload-area');
      if (uploadArea) {
        const navbarHeight = 64;
        const additionalOffset = 80; // Perfect offset for reload
        const elementPosition = uploadArea.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - navbarHeight - additionalOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [conversionType, setSelectedConversion]);

  const metadata = getConversionMeta(conversionType);
  const baseUrl = 'https://iconvertify.com';
  
  // CRITICAL FIX: Canonical URL must ALWAYS match the current page URL
  // This prevents "Alternate page with proper canonical tag" issues in Google Search Console
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  const ogImage = metadata.openGraph?.image || `${baseUrl}/favicon-assets/android-chrome-512x512.png`;
  const twitterImage = metadata.twitter?.image || ogImage;

  // Only set Helmet metadata when NOT rendering ImagesToPdf or PdfToImages
  // Those components handle their own metadata
  const shouldSetMetadata = !isImagesToPdf && !isPdfToImages;

  return (
    <>
      {shouldSetMetadata && (
        <Helmet>
          <title>{metadata.title}</title>
          <meta name="description" content={metadata.description} />
          <meta name="keywords" content={metadata.keywords} />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          
          {/* OpenGraph */}
          <meta property="og:title" content={metadata.openGraph?.title || metadata.title} />
          <meta property="og:description" content={metadata.openGraph?.description || metadata.description} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="512" />
          <meta property="og:image:height" content="512" />
          <meta property="og:image:alt" content={metadata.openGraph?.title || metadata.title} />
          <meta property="og:type" content={metadata.openGraph?.type || 'website'} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:site_name" content="iconvertify" />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content={metadata.twitter?.card || 'summary_large_image'} />
          <meta name="twitter:title" content={metadata.twitter?.title || metadata.title} />
          <meta name="twitter:description" content={metadata.twitter?.description || metadata.description} />
          <meta name="twitter:image" content={twitterImage} />
          <meta name="twitter:image:alt" content={metadata.twitter?.title || metadata.title} />
          
          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(metadata.schema)}
          </script>
          {/* Breadcrumb Structured Data */}
          {conversionType && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://iconvertify.com"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": metadata.title.split('|')[0].trim(),
                    "item": canonicalUrl
                  }
                ]
              })}
            </script>
          )}
        </Helmet>
      )}

      <div className={`min-h-screen transition-colors duration-300
        ${darkMode ? 'bg-gray-950' : 'bg-gray-100'}
      `}>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-neutral-900 dark:text-neutral-100">
                Convert Your Files with
                <span className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-400 bg-clip-text text-transparent
                  dark:drop-shadow-[0_2px_16px_rgba(16,185,129,0.25)]
                  dark:shadow-primary-400/30
                  dark:filter dark:brightness-125 dark:contrast-125
                ">
                  {' '}Confidence
                </span>
              </h1>
              
              <div className="space-y-4">
                <p className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto text-neutral-600/90 dark:text-neutral-300">
                  Secure, fast, and reliable file conversion for all your needs.{' '}
                  <span className="inline-flex items-center gap-1.5 font-medium
                    text-neutral-800 dark:text-neutral-100
                    dark:bg-gray-900/40 dark:px-2 dark:py-0.5 dark:rounded-lg dark:shadow-[0_2px_8px_-2px_rgba(16,185,129,0.10)]
                  ">
                    <span className="dark:text-emerald-300">No registration required</span>
                    <motion.span
                      initial={{ opacity: 0.5 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
                    >
                      <FiCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-300 drop-shadow-[0_1px_4px_rgba(16,185,129,0.25)]" />
                    </motion.span>
                  </span>
                </p>
              </div>
            </motion.div>
          </div>

          <div className="w-full max-w-lg mx-auto mb-8">
            <div className={`h-px bg-gradient-to-r from-transparent to-transparent
              ${darkMode ? 'via-gray-800' : 'via-gray-200'}`}
            />
          </div>

          <div id="file-upload-area">
            {conversionTitle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-8"
              >
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-800 dark:text-gray-100">
                  Convert {conversionTitle.from} to {conversionTitle.to}
                </h2>
              </motion.div>
            )}
            {isImagesToPdf ? (
              <ImagesToPdf />
            ) : isPdfToImages ? (
              <PdfToImages setProgress={() => {}} />
            ) : (
              <FileUpload />
            )}
          </div>

          {/* Trust indicators */}
          <div className="flex justify-center gap-8 mt-8">
            <div className="flex items-center gap-2 text-neutral-600/90 dark:text-neutral-300">
              <div className="p-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30">
                <FiLock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-sm">End-to-End Encryption</span>
            </div>
            <div className="flex items-center gap-2 text-neutral-600/90 dark:text-neutral-300">
              <div className="p-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30">
                <FiCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-sm">No Registration Required</span>
            </div>
            <div className="flex items-center gap-2 text-neutral-600/90 dark:text-neutral-300">
              <div className="p-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30">
                <FiCloud className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-sm">Instant Processing</span>
            </div>
          </div>

          <HowItWorks darkMode={false} />

          <section className={`py-12 sm:py-16 md:py-20 transition-colors duration-300
            ${darkMode ? 'bg-gradient-to-b from-gray-900 to-gray-950' : 'bg-gradient-to-b from-gray-50 to-white'}
          `}>
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8
              ${darkMode ? 'bg-gray-950 rounded-2xl shadow-lg' : ''}`}
            >
              <div className="text-center mb-12">
                <span className={`font-medium tracking-wider text-sm uppercase block mb-2
                  ${darkMode ? 'text-primary-400' : 'text-primary-600'}`}>File Conversion Tools</span>
                <h2 className={`text-3xl font-bold mb-4
                  ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Convert Any File Format</h2>
                <p className={`max-w-2xl mx-auto
                  ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Professional-grade conversions for all your document, image, and media needs.</p>
              </div>

              {/* Replace the scrolling container with a grid */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8
                ${darkMode ? 'bg-gray-950 rounded-2xl shadow-lg p-4' : ''}`}
              >
                {/* Column 1: Documents */}
                <div className="space-y-8">
                  <ConversionCategory title="DOCUMENT" items={conversionOptions.document} />
                </div>

                {/* Column 2: Images + Other */}
                <div className="space-y-8">
                  <ConversionCategory title="IMAGE" items={conversionOptions.image} />
                  <ConversionCategory title="OTHER" items={conversionOptions.other} />
                </div>

                {/* Column 3: AudioVideo + Data */}
                <div className="space-y-8">
                  <ConversionCategory title="AUDIOVIDEO" items={conversionOptions.audioVideo} />
                  <ConversionCategory title="DATA" items={conversionOptions.data} />
                </div>

                {/* Column 4: Archive + Ebook + CadDesign */}
                <div className="space-y-8">
                  <ConversionCategory title="ARCHIVE" items={conversionOptions.archive} />
                  <ConversionCategory title="EBOOK" items={conversionOptions.ebook} />
                  <ConversionCategory title="CADDESIGN" items={conversionOptions.cadDesign} />
                </div>
              </div>
            </div>
          </section>

          <section className={`py-20 transition-colors duration-300
  ${darkMode ? 'bg-gradient-to-b from-gray-950 to-gray-900' : 'bg-gradient-to-b from-white to-gray-50'}
`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
      <span className={`font-medium tracking-wider text-sm uppercase block mb-3
        ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Mathematical Precision</span>
      <h2 className={`text-4xl font-bold mb-4
        ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Advanced Unit Conversion System</h2>
      <p className={`max-w-2xl mx-auto text-lg
        ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>More than just conversions - a complete mathematical toolkit with <span className={darkMode ? 'text-neutral-100' : 'text-gray-900'}>interactive learning features</span></p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                {/* Left side: Feature highlights */}
                <div className="space-y-6">
                  <motion.div 
                    whileHover={{ x: 5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
          className={`group relative p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300
            ${darkMode ? 'bg-gray-900/90 text-neutral-100 border border-gray-800/80 shadow-lg' : 'bg-white'}
          `}
                  >
                    <div className="flex items-start gap-4">
                      <motion.div 
                        initial={{ rotate: 0 }}
                        whileHover={{ rotate: 15 }}
              className={`p-3 rounded-lg bg-gradient-to-br
                ${darkMode ? 'from-emerald-900 to-emerald-800 border border-emerald-800/60 shadow-[0_2px_16px_rgba(16,185,129,0.10)]' : 'from-emerald-50 to-emerald-100'}`}
                      >
                        <FiZap className="w-6 h-6 text-emerald-600" />
                      </motion.div>
                      <div>
              <h3 className={`font-semibold mb-2 flex items-center gap-2
                ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Interactive Learning
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                  ${darkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>Exclusive</span>
                        </h3>
              <p className={`leading-relaxed ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Watch conversions unfold step-by-step with detailed explanations and visual breakdowns.</p>
                      </div>
                    </div>
          <div className={`absolute inset-x-0 bottom-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity
            ${darkMode ? 'bg-gradient-to-r from-transparent via-emerald-900/30 to-transparent' : 'bg-gradient-to-r from-transparent via-emerald-200/30 to-transparent'}`}
          />
                  </motion.div>
                  <motion.div 
                    whileHover={{ x: 5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
          className={`group relative p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300
            ${darkMode ? 'bg-gray-900/90 text-neutral-100 border border-gray-800/80 shadow-lg' : 'bg-white'}
          `}
                  >
                    <div className="flex items-start gap-4">
                      <motion.div 
                        initial={{ rotate: 0 }}
                        whileHover={{ rotate: 15 }}
              className={`p-3 rounded-lg bg-gradient-to-br
                ${darkMode ? 'from-violet-900 to-violet-800 border border-violet-800/60 shadow-[0_2px_16px_rgba(139,92,246,0.10)]' : 'from-violet-50 to-violet-100'}`}
                      >
                        <FiCode className="w-6 h-6 text-violet-600" />
                      </motion.div>
                      <div>
              <h3 className={`font-semibold mb-2 ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Advanced Number Systems</h3>
              <p className={`leading-relaxed ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Convert between binary, decimal, hexadecimal, and more with comprehensive binary operations support.</p>
                      </div>
                    </div>
          <div className={`absolute inset-x-0 bottom-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity
            ${darkMode ? 'bg-gradient-to-r from-transparent via-violet-900/30 to-transparent' : 'bg-gradient-to-r from-transparent via-violet-200/30 to-transparent'}`}
          />
                  </motion.div>
                  <motion.div 
                    whileHover={{ x: 5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
          className={`group relative p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300
            ${darkMode ? 'bg-gray-900/90 text-neutral-100 border border-gray-800/80 shadow-lg' : 'bg-white'}
          `}
                  >
                    <div className="flex items-start gap-4">
                      <motion.div 
                        initial={{ rotate: 0 }}
                        whileHover={{ rotate: 15 }}
              className={`p-3 rounded-lg bg-gradient-to-br
                ${darkMode ? 'from-blue-900 to-blue-800 border border-blue-800/60 shadow-[0_2px_16px_rgba(59,130,246,0.10)]' : 'from-blue-50 to-blue-100'}`}
                      >
                        <FiLayout className="w-6 h-6 text-blue-600" />
                      </motion.div>
                      <div>
              <h3 className={`font-semibold mb-2 ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Visual Learning Tools</h3>
              <p className={`leading-relaxed ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Toggle between different views, encoding modes, and padding options. See how each choice affects the conversion.</p>
                      </div>
                    </div>
          <div className={`absolute inset-x-0 bottom-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity
            ${darkMode ? 'bg-gradient-to-r from-transparent via-blue-900/30 to-transparent' : 'bg-gradient-to-r from-transparent via-blue-200/30 to-transparent'}`}
          />
                  </motion.div>
                </div>
                {/* Right side: Interactive Preview */}
                <div className="relative">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl shadow-lg overflow-hidden flex flex-col transition-colors duration-300
            ${darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'}
          `}
                  >
                    {/* Preview Header */}
          <div className={`p-4 border-b flex-shrink-0
            ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100'}`}
          >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full
                  ${darkMode ? 'bg-red-800' : 'bg-red-400'}`} />
                <div className={`w-3 h-3 rounded-full
                  ${darkMode ? 'bg-yellow-700' : 'bg-yellow-400'}`} />
                <div className={`w-3 h-3 rounded-full
                  ${darkMode ? 'bg-green-800' : 'bg-green-400'}`} />
                        </div>
              <div className={`text-sm font-medium
                ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Live Preview</div>
                        </div>
                      </div>
                    {/* Scrollable Preview Content */}
                    <div className="relative flex-1">
            <div className={`h-[480px] overflow-y-auto group
              ${darkMode ? 'bg-gray-900' : ''}
            `}>
                        <div className="p-6">
                          <ExplanationShowcase />
                        </div>
                      </div>
                      {/* Enhanced, more subtle gradient fades */}
            <div className={`absolute bottom-0 left-0 right-0 h-20 pointer-events-none transition-opacity duration-300
              ${darkMode ? 'bg-gradient-to-t from-gray-900 via-gray-900/90 to-transparent group-hover:opacity-90' : 'bg-gradient-to-t from-white via-white/90 to-transparent group-hover:opacity-90'}`}
                      />
            <div className={`absolute top-0 left-0 right-0 h-12 pointer-events-none transition-opacity duration-300
              ${darkMode ? 'bg-gradient-to-b from-gray-900 via-gray-900/90 to-transparent group-hover:opacity-90' : 'bg-gradient-to-b from-white via-white/90 to-transparent group-hover:opacity-90'}`}
                      />
                    </div>
                    {/* Preview Footer */}
          <div className={`px-6 py-4 flex-shrink-0
            ${darkMode ? 'bg-gray-900 border-t border-gray-800' : 'bg-gray-50 border-t border-gray-100'}`}
          >
                      <div className="flex justify-end">
                        <Link
                          to="/unit-conversions"
                className={`inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200
                  ${darkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}
                        >
                          Try it now
                          <FiArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                  {/* Decorative Elements */}
        <div className={`absolute -z-10 inset-0 blur-xl opacity-50 transform translate-y-8 scale-95
          ${darkMode ? 'bg-gradient-to-r from-emerald-900 via-emerald-800/50 to-emerald-900' : 'bg-gradient-to-r from-emerald-50 via-emerald-100/50 to-emerald-50'}`}
        />
                </div>
              </div>
            </div>
          </section>

          {/* Update the footer section with a cleaner, more minimal design */}
          <footer className={`border-t transition-colors duration-300
  ${darkMode ? 'border-gray-800 bg-gradient-to-b from-gray-950 to-gray-900 shadow-[0_-4px_32px_-8px_rgba(16,185,129,0.10)]' : 'border-gray-100 bg-gradient-to-b from-white to-gray-50/80'}
`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              {/* Main Footer Content */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {/* Brand Column */}
                <div className="space-y-4">
                  <Link to="/" className="inline-block">
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-lg font-['Inter'] tracking-tight">
              <span className={`font-extrabold ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>icon</span>
              <span className="font-normal bg-gradient-to-r from-primary-600 via-primary-500 to-primary-400 bg-clip-text text-transparent transition-all duration-300
                dark:from-primary-400 dark:via-primary-300 dark:to-primary-200">vertify</span>
                      </span>
                    </div>
                  </Link>
        <p className={`text-sm leading-relaxed ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Professional file and unit conversion tools, designed for precision and ease of use.</p>
                </div>
                {/* Essential Links */}
                <div className="space-y-4">
        <h3 className={`text-sm font-semibold ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Essential</h3>
                  <ul className="space-y-3">
                    {[
                      ['Privacy', '/privacy']
                    ].map(([title, path]) => (
                      <li key={path}>
                        <Link 
                          to={path} 
                className={`inline-flex items-center gap-2 text-sm transition-colors duration-200
                  ${darkMode ? 'text-neutral-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-600'}`}
                        >
                          {title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Features Column */}
                <div className="space-y-4">
        <h3 className={`text-sm font-semibold ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Features</h3>
                  <ul className="space-y-3">
                    {[
                      ['File Conversion', '/'],
                      ['Unit Conversion', '/unit-conversions', 'New']
                    ].map(([title, path, badge]) => (
                      <li key={path}>
                        <Link 
                          to={path} 
                className={`inline-flex items-center gap-2 text-sm group transition-colors duration-200
                  ${darkMode ? 'text-neutral-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-600'}`}
                        >
                          {title}
                          {badge && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full
                    ${darkMode ? 'bg-gradient-to-r from-primary-900 to-primary-800 text-primary-300' : 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-600'}`}>{badge}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Bottom Section */}
    <div className={`mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4
      ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}
    >
                  {/* Left side with logo and copyright */}
      <div className={`flex items-center gap-3 text-sm
        ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}
      >
                    <div className="flex items-center gap-2">
                      <motion.div 
                        whileHover={{ rotate: 360 }} 
                        transition={{ duration: 0.7 }}
            className={darkMode ? 'text-primary-400' : 'text-primary-500'}
                      >
                        <DE className="w-4 h-4" />
                      </motion.div>
                      <span>Made in Germany</span>
                    </div>
        <span className={darkMode ? 'text-gray-700' : 'text-gray-300'}>•</span>
                    <span>© 2024 iconvertify</span>
                  </div>
                  {/* Right side with GitHub */}
                  <motion.a
                    href="https://github.com"
                    target="_blank"
                    rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors duration-200
          ${darkMode ? 'text-neutral-400 hover:text-primary-400 hover:bg-gray-800' : 'text-gray-500 hover:text-primary-600 hover:bg-gray-50'}`}
                    whileHover={{ y: -1 }}
                    whileTap={{ y: 0 }}
                  >
                    <FiGithub className="w-4 h-4" />
                    <span>View on GitHub</span>
                  </motion.a>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Home;